// @ts-nocheck
import React, { useState } from "react";
import { differenceInDays } from "date-fns";

import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import api from "../../../services/api";
import { useRequirement } from "../../../hooks/requirement";
import { useToast } from "../../../hooks/toast";
import ExigenciaLinhaTempo, { ExigenciaNaoERecusa, exigenciaAtual, formatarData, somarMeses } from "../ExigenciaLinhaTempo";
import ComponentHistoric from "../CardFollowUp/Historic";

import {
  Corpo,
  Selo,
  Titulo,
  Aviso,
  Blocos,
  Bloco,
  Progresso,
  Botao,
  BotaoLink,
  LinkTexto,
  Secao,
  Itens,
  Item,
  Recolhido,
  Ajuda,
  Campo,
  linkWhatsApp,
} from "./ui";

const LINK_JUSTICA = "https://meu.registo.justica.gov.pt/Pedidos/Consultar-estado-do-processo-de-nacionalidade";
const LINK_RASTREIO = "https://www.dhl.com/br-pt/home/tracking.html";
const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const mesAno = (data) => {
  const [ano, mes] = String(data || "").slice(0, 10).split("-").map(Number);
  return ano && mes ? { mes: MESES[mes - 1], ano } : null;
};

// Previsão de conclusão em Portugal ("Março de 2027" ou "Entre Março e Maio de 2027").
// Produto sem prazo (0 e 0) gera previsão igual à data de envio: sem previsão.
function previsaoConclusao(process) {
  const dia = (d) => (d ? String(d).slice(0, 10) : null);
  const semPrevisao =
    !process?.final_prediction_end_date ||
    (dia(process?.initial_prediction_end_date) === dia(process?.post_date) && dia(process?.final_prediction_end_date) === dia(process?.post_date));
  if (semPrevisao) return { texto: "Será informada em breve", atrasada: false };

  const ini = mesAno(process.initial_prediction_end_date);
  const fim = mesAno(process.final_prediction_end_date);
  const texto =
    !ini || (ini.mes === fim.mes && ini.ano === fim.ano)
      ? `${fim.mes} de ${fim.ano}`
      : ini.ano === fim.ano
      ? `Entre ${ini.mes} e ${fim.mes} de ${fim.ano}`
      : `Entre ${ini.mes} de ${ini.ano} e ${fim.mes} de ${fim.ano}`;
  const atrasada = differenceInDays(new Date(), new Date(process.final_prediction_end_date)) > 0;
  return { texto, atrasada };
}

// Passos do pedido na conservatória (current_step / total_steps), quando houver.
function PassosConservatoria({ process }) {
  const total = Number(process?.total_steps);
  const atual = Number(process?.current_step);
  if (!(total > 0) || !(atual > 0)) return null;
  return <Progresso rotulo={`Passo ${Math.min(atual, total)} de ${total} na conservatória`} percentual={(Math.min(atual, total) / total) * 100} />;
}

// Senha de acesso e botão do site da Justiça (ou "ainda não disponível").
function Senha({ process }) {
  if (process?.type === "Transcrição de Casamento") return null;
  if (!process?.access_password) {
    return (
      <Campo rotulo="Senha de acesso" vazio="Ainda não disponível" iconeVazio={<LockOutlinedIcon style={{ color: "var(--cinza-50)", marginRight: 12 }} />} />
    );
  }
  return (
    <>
      <Campo rotulo="Senha de acesso" valor={process.access_password} />
      <BotaoLink href={LINK_JUSTICA} target="_blank" rel="noreferrer">
        Consultar no site da Justiça
        <OpenInNewIcon />
      </BotaoLink>
    </>
  );
}

// Comentários da equipe, recolhidos no fim do card.
export function Historico({ process }) {
  const comentarios = process?.comments || [];
  if (!comentarios.length) return null;
  return (
    <Recolhido rotulo={`Ver histórico do pedido (${comentarios.length})`} icone={null}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {comentarios.map((item, i) => (
          <ComponentHistoric key={item?.id || i} data={item} />
        ))}
      </div>
    </Recolhido>
  );
}

// Em Portugal (monitoring, approved, dispatched).
export default function Portugal({ process }) {
  const temSenha = !!process?.access_password;
  const transcricao = process?.type === "Transcrição de Casamento";
  const previsao = previsaoConclusao(process);
  const enviadoEm = formatarData(process?.post_date);
  const diasDoEnvio = differenceInDays(new Date(), new Date(process?.post_date));
  const mostrarRastreio = !temSenha && process?.post_code && diasDoEnvio > 0 && diasDoEnvio <= 90;

  return (
    <Corpo>
      <Selo tipo="equipe" />
      <Titulo>{temSenha || transcricao ? "Seu pedido está na conservatória" : "Seu pedido foi enviado a Portugal"}</Titulo>
      <p className="desc">
        {transcricao
          ? "Nada a fazer agora. A conservatória não abre consulta pública para a transcrição: nós acompanhamos e avisamos aqui."
          : temSenha
          ? "Nada a fazer agora. Com a senha abaixo, você pode consultar o andamento direto no site da Justiça de Portugal."
          : "O processo está na conservatória. Quando ela registrar o pedido, a senha de acesso aparece aqui."}
      </p>

      {previsao.atrasada && (
        <Aviso tipo="cliente">
          <b>Está levando mais tempo que o previsto</b>
          <p>
            Não controlamos todas as etapas em Portugal e a previsão vem do histórico dos nossos clientes. Assim que a conservatória tiver novidade, avisamos
            aqui{temSenha ? "; com a senha, você também acompanha no site da Justiça" : ""}.
          </p>
        </Aviso>
      )}

      <Blocos>
        <Bloco rotulo="Enviado em">{enviadoEm || "—"}</Bloco>
        <Bloco rotulo={previsao.atrasada ? "Previsão inicial" : "Previsão de conclusão"}>
          {previsao.atrasada ? <s>{previsao.texto}</s> : previsao.texto}
        </Bloco>
      </Blocos>

      <PassosConservatoria process={process} />

      {mostrarRastreio && (
        <>
          <Campo rotulo="Código de rastreio" valor={process.post_code} />
          <LinkTexto href={LINK_RASTREIO} target="_blank" rel="noreferrer">
            Acompanhar rastreio <ChevronRightIcon />
          </LinkTexto>
        </>
      )}

      <Senha process={process} />

      {transcricao ? (
        <Ajuda titulo="O que vem depois" passos={["A conservatória registra a transcrição.", "O assento de casamento português fica disponível aqui."]} />
      ) : temSenha ? (
        <Ajuda titulo="Como consultar" passos={["Copie a senha acima.", "Toque em “Consultar no site da Justiça”.", "Cole a senha no campo “senha de acesso” e confirme."]}>
          <p>O site é oficial de Portugal e em português de lá. As atualizações também chegam aqui na Central.</p>
        </Ajuda>
      ) : (
        <Ajuda
          titulo="O que vem depois"
          passos={["A conservatória registra o pedido e gera a senha de acesso.", "Com a senha, você acompanha o pedido direto no site da Justiça de Portugal."]}
        />
      )}

      <Historico process={process} />
    </Corpo>
  );
}

// O sync antigo gravava a descrição como booleano ("1"): nesse caso usa o título.
const motivo = (exigencia) => {
  const texto = String(exigencia?.reason_description ?? "").trim();
  return texto && texto !== "1" && texto !== "0" ? texto : exigencia?.reason_title;
};

// Exigência da conservatória (requirement).
export function Exigencia({ process, action }) {
  const { addRequirement } = useRequirement();
  const { addToast } = useToast();
  const [enviando, setEnviando] = useState(false);
  const exigencia = exigenciaAtual(process?.requirements);
  const categoria = exigencia?.solution?.category?.name;
  const respondida = !!exigencia?.answer_sent_at;
  const documento = categoria === "INFO_DOCUMENT" ? exigencia?.document : null;
  const novaVenda = categoria === "NEW_SALE";
  const vezDoCliente = !respondida && (!!documento || novaVenda);
  const meses = Number(exigencia?.expected_months);
  const retorno = somarMeses(exigencia?.answer_sent_at, meses);

  const abrirDocumento = () => {
    addRequirement({
      ...documento,
      open: true,
      proxy_form_path: process?.proxy_form_path,
      process_id: process?.id,
      identifier: process?.identifier,
      proxy_path: process?.proxy_path,
    });
    action && action();
  };

  async function pedirContato() {
    try {
      setEnviando(true);
      await api.post("central/request-new-sale-contact", { process_id: process?.id });
      addToast({ type: "success", title: "Pedido enviado", description: "O seu consultor vai entrar em contato." });
    } catch (err) {
      addToast({ type: "error", title: "Não foi possível enviar", description: "Tente de novo em instantes." });
    } finally {
      setEnviando(false);
    }
  }

  let titulo = "A conservatória pediu um complemento";
  let frase = "Nossa equipe está cuidando disso e, se precisarmos de algo seu, avisamos aqui. Nada a fazer agora.";
  if (respondida) {
    titulo = "Respondemos a conservatória";
    frase = "Enviamos o que a conservatória pediu. Agora é aguardar o retorno dela. Nada a fazer agora.";
  } else if (vezDoCliente) {
    titulo = "A conservatória pediu um documento seu";
    frase = novaVenda
      ? "Para responder a exigência, é preciso contratar um serviço a mais. Peça o contato do seu consultor para combinar."
      : "Para responder a exigência, precisamos do documento abaixo. Assim que ele chegar, respondemos a conservatória.";
  }

  return (
    <Corpo>
      <Selo tipo={vezDoCliente ? "cliente" : "equipe"} />
      <Titulo>{titulo}</Titulo>
      <p className="desc">{frase}</p>

      {exigencia && !respondida && motivo(exigencia) && (
        <Aviso tipo="neutro">
          <b>O que foi pedido</b>
          <p>{motivo(exigencia)}</p>
        </Aviso>
      )}

      {respondida && (
        <Blocos>
          <Bloco rotulo="Resposta enviada">{formatarData(exigencia.answer_sent_at)}</Bloco>
          <Bloco rotulo="Previsão de retorno">{retorno ? `Até ${meses} ${meses === 1 ? "mês" : "meses"} (${retorno})` : "Informada pela conservatória"}</Bloco>
        </Blocos>
      )}

      {vezDoCliente && documento && (
        <>
          <Secao>Você envia · 1</Secao>
          <Itens>
            <Item
              tipo="cliente"
              icone={documento.name === "Procuração assinada" ? <EditOutlinedIcon /> : <DescriptionOutlinedIcon />}
              titulo={documento.name}
              descricao={[documento.format, documento.specification].filter(Boolean).join(" · ")}
            >
              <LinkTexto as="button" type="button" onClick={abrirDocumento}>
                {documento.name === "Procuração assinada"
                  ? process?.proxy_path
                    ? "Edite e baixe sua procuração"
                    : "Preencher procuração"
                  : "Saiba sobre o formato e como emitir"}
                <ChevronRightIcon />
              </LinkTexto>
            </Item>
          </Itens>
        </>
      )}

      {vezDoCliente && novaVenda && (
        <Botao type="button" disabled={enviando} onClick={pedirContato}>
          {enviando ? "Enviando…" : "Solicitar contato do consultor"}
        </Botao>
      )}

      {exigencia ? <ExigenciaLinhaTempo process={process} requirement={exigencia} /> : null}
      <ExigenciaNaoERecusa />

      <Senha process={process} />

      <BotaoLink
        secundario
        href={linkWhatsApp(`Olá, tudo bem? Meu pedido ${process?.service_code || ""} está com uma exigência, poderia me ajudar?`)}
        target="_blank"
        rel="noreferrer"
      >
        Fale com um especialista
      </BotaoLink>

      <Historico process={process} />
    </Corpo>
  );
}
